/**
 * Leitfaden der Planungsphase — pure Regeln (docs/planungsphase-leitfaden-plan.md,
 * Stufe L0).
 *
 * Der Leitfaden ist eine ANSICHT, kein Zustand: jede der neun Stationen
 * rechnet ihren Stand aus dem GESPEICHERTEN Kopfsatz, den Fragen, Hinweisen
 * und Unterlagen. Es gibt keinen gespeicherten „Schritt 4 von 9" — deshalb
 * kann man jederzeit hinaus, und beim naechsten Oeffnen steht die Station von
 * selbst wieder da, an der etwas zu tun ist.
 *
 * Hier wird NICHTS geschrieben und keine Fachregel nachgebaut. Wer was
 * deckt, entscheidet `pruefeFreigabe`; Fristen liest `istUeberfaellig` bzw.
 * `faelligeStatuswechsel`; Pflichten kommen live ueber `pflichtenStand`;
 * die Checkliste baut `baueUnterlagenZeilen`; Nachziehen und gegenstandslos
 * zaehlen `neueFragenNachziehen` und `nichtMehrZutreffend`. Aendert sich dort
 * eine Regel, folgt der Leitfaden von selbst — genau wie die Ampel
 * (`planungAmpel.ts`), deren harte Gruende er als `handlungsbedarf` zeigt.
 */

import type { PflichtArt, Planungsfrage, Planungshinweis, Planungsphase, ProjektMerkmale } from './types';
import {
    STATION_REIHENFOLGE,
    type LeitfadenAbschnitt,
    type LeitfadenEingang,
    type StationSchluessel,
    type StationStand,
    type StationZustand,
} from './leitfadenTypen';
import { MERKMAL_SCHLUESSEL } from './grundlagen/merkmalGruppen';
import {
    faelligeStatuswechsel,
    fristDatum,
    istUeberfaellig,
    neueFragenNachziehen,
    nichtMehrZutreffend,
    PFLICHT_ART_LABEL,
    pflichtenStand,
    pruefeFreigabe,
} from './planungLogic';
import { ROT_FENSTER_TAGE, vorankuendigungFrist } from './planungAmpel';
import { baueUnterlagenZeilen, datumDeutsch, istUnerledigt, unterlagenFristUeberfaellig } from './unterlagenLogic';
import { STATION_TEXTE } from './leitfadenTexte';

// ═══════════════════════════════════════════════════════════════════════════
// STATIONEN
// ═══════════════════════════════════════════════════════════════════════════

/** A Vorhaben (1–3) · B Fragen (4–6) · C Nachweise (7–8) · D Abschluss (9). */
export const STATION_ABSCHNITT: Record<StationSchluessel, LeitfadenAbschnitt> = {
    beschreiben: 'A',
    umfang: 'A',
    merkmale: 'A',
    katalog: 'B',
    versenden: 'B',
    antworten: 'B',
    unterlagen: 'C',
    bedenken: 'C',
    abschluss: 'D',
};

/** Ist der Wert einer der neun Stationsschluessel? Fuer alles, was aus dem
 *  Browser zurueckkommt — dort kann jede Zeichenkette stehen. */
export const istStationSchluessel = (s: unknown): s is StationSchluessel =>
    typeof s === 'string' && (STATION_REIHENFOLGE as readonly string[]).includes(s);

/** Vorgaenger (`-1`) bzw. Nachfolger (`1`) in der Ablaufreihenfolge, `null`
 *  am Rand — fuer „Zurueck" und „Weiter" in der Fussleiste. */
export const nachbarStation = (s: StationSchluessel, richtung: 1 | -1): StationSchluessel | null => {
    const i = STATION_REIHENFOLGE.indexOf(s);
    if (i < 0) return null;
    return STATION_REIHENFOLGE[i + richtung] ?? null;
};

// ═══════════════════════════════════════════════════════════════════════════
// MERKMALE
// ═══════════════════════════════════════════════════════════════════════════

/** Die Ja/Nein-Merkmale der Maske (`MERKMAL_SCHLUESSEL`), die weder `true`
 *  noch `false` sind — also „offen" (dreiwertig, siehe `ProjektMerkmale`). */
export const offeneMerkmale = (merkmale: ProjektMerkmale | null | undefined): (keyof ProjektMerkmale)[] =>
    MERKMAL_SCHLUESSEL.filter(k => {
        const v = merkmale ? merkmale[k] : undefined;
        return v !== true && v !== false;
    });

/**
 * Stabile Kurzform des Merkmalsstands fuer „Rest bleibt offen" (Entscheid
 * E-L6): je Schluessel aus `MERKMAL_SCHLUESSEL` in fester Reihenfolge ein
 * Zeichen, `j` = ja, `n` = nein, `o` = offen. Zahlen, Gebaeudetyp und der
 * Dritte nach § 4 bleiben aussen vor — sie aendern nichts daran, welche
 * Merkmale offen sind, und sollen die Bestaetigung nicht aufheben.
 */
export const merkmaleFingerabdruck = (merkmale: ProjektMerkmale | null | undefined): string =>
    MERKMAL_SCHLUESSEL.map(k => {
        const v = merkmale ? merkmale[k] : undefined;
        return v === true ? 'j' : v === false ? 'n' : 'o';
    }).join('');

/**
 * Gilt der Vermerk „Rest bleibt offen" (`ProjektMerkmale.leitfadenRestOffen`)?
 * Nur, wenn er genau zum Merkmalsstand DIESER Merkmale passt: aendert sich
 * ein Ja/Nein-Merkmal, ist der Fingerabdruck ein anderer und der Vermerk
 * wirkungslos, bis er neu bestaetigt wird. Gelesen am GESPEICHERTEN Satz —
 * dieselbe Regel fuer Modul, Hub-Kachel, Dashboard und Aufgabenliste.
 */
export const restOffenVermerkt = (merkmale: ProjektMerkmale | null | undefined): boolean => {
    const vermerk = merkmale ? merkmale.leitfadenRestOffen : undefined;
    return typeof vermerk === 'string' && vermerk !== '' && vermerk === merkmaleFingerabdruck(merkmale);
};

/** Die Merkmale mit dem Vermerk „Rest bleibt offen" fuer ihren eigenen
 *  Stand — alle anderen Schluessel bleiben, wie sie sind. Was „Rest bleibt
 *  offen" als Teil-Update an den Kopfsatz schreibt. */
export const mitRestOffenVermerk = (merkmale: ProjektMerkmale | null | undefined): ProjektMerkmale => ({
    ...(merkmale || {}),
    leitfadenRestOffen: merkmaleFingerabdruck(merkmale),
});

// ═══════════════════════════════════════════════════════════════════════════
// HELFER
// ═══════════════════════════════════════════════════════════════════════════

/** „1 Frage" / „2 Fragen" — die Zahl steht immer vorn. */
const zahlwort = (n: number, einzahl: string, mehrzahl: string): string =>
    `${n} ${n === 1 ? einzahl : mehrzahl}`;

const istIsoTag = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s);

/** Fruehester gueltiger ISO-Tag (YYYY-MM-DD) der Liste, sonst `null`. ISO-Tage
 *  sind lexikografisch vergleichbar — kein Date-Parsing. */
const fruehesterTag = (tage: ReadonlyArray<string | null | undefined>): string | null =>
    tage.map(t => String(t || '').slice(0, 10)).filter(istIsoTag).sort()[0] ?? null;

const unerledigt = (f: Planungsfrage): boolean =>
    f.status !== 'beantwortet' && f.status !== 'gegenstandslos';

const SPERRGRUND_KATALOG = 'Erst den Fragenkatalog erzeugen (Station 4).';
const SPERRGRUND_GRUNDLAGEN = 'Erst die Grundlagen erfassen (Station 1).';

/** Die drei Schwellen-Pflichten der Station 2 — dieselben, die die Ampel rot
 *  machen koennen. UfsA haengt am Koordinator, der A+S-Verdacht ist keine
 *  Pflicht nach BaustellV. */
const UMFANG_PFLICHTEN: ReadonlyArray<PflichtArt> = ['vorankuendigung', 'sigeplan', 'koordinator'];

/** Was eine Station ueber sich selbst sagt; `voll` macht daraus den Stand. */
interface Teilstand {
    zustand: StationZustand;
    kurz: string;
    hinweis?: boolean;
    sperrgrund?: string;
    naechsteFrist?: string | null;
}

/** Alles, was mehrere Stationen lesen — einmal gerechnet. */
interface Lage {
    e: LeitfadenEingang;
    heute: string;
    phase: Planungsphase | null;
    fragen: Planungsfrage[];
    hinweise: Planungshinweis[];
}

// ═══════════════════════════════════════════════════════════════════════════
// REGELN JE STATION
// ═══════════════════════════════════════════════════════════════════════════

const beschreiben = ({ phase }: Lage): Teilstand =>
    phase && String(phase.baubeschreibung || '').trim()
        ? { zustand: 'erledigt', kurz: '' }
        : { zustand: 'offen', kurz: 'Noch keine Baubeschreibung gespeichert' };

/**
 * Station 2. `pruefung` steht nur als Zeichen, dass die Grundlagen einmal
 * gespeichert wurden; der Pflichtstand selbst kommt LIVE aus den Merkmalen
 * (`pflichtenStand`), wie bei der Ampel. Eine ungeklaerte Pflicht ist nach
 * Entscheid E-L5 erlaubt (Weiter geht) — rot wird sie erst im Fenster vor der
 * Vorankuendigungsfrist und nie nach dem Abschluss, dieselbe Regel wie
 * `planungAmpel`.
 */
const umfang = ({ e, phase, heute }: Lage): Teilstand => {
    if (!phase || !phase.pruefung) {
        return { zustand: 'offen', kurz: 'Bauzeit, Beschäftigte und Personentage erfassen' };
    }
    const pflichten = pflichtenStand(phase, `${heute}T00:00:00Z`).filter(p => UMFANG_PFLICHTEN.includes(p.art));
    const ungeklaert = pflichten.filter(p => p.stand === 'ungeklaert').map(p => PFLICHT_ART_LABEL[p.art]);
    if (!ungeklaert.length) {
        const pflicht = pflichten.filter(p => p.stand === 'pflicht').map(p => PFLICHT_ART_LABEL[p.art]);
        return {
            zustand: 'erledigt',
            kurz: pflicht.length ? `${pflicht.join(', ')}: erforderlich` : 'keine Pflicht nach BaustellV',
        };
    }
    const liste = ungeklaert.join(', ');
    const frist = vorankuendigungFrist(e.baustartIso);
    const beginn = frist ? fristDatum(frist, -ROT_FENSTER_TAGE) : '';
    const imRotFenster = !!frist && !!beginn && istIsoTag(heute) && heute >= beginn;
    if (imRotFenster && frist && phase.status !== 'abgeschlossen') {
        return {
            zustand: 'handlungsbedarf',
            kurz: `Pflicht ungeklärt (${liste}) — Vorankündigungsfrist ${datumDeutsch(frist) || frist}`,
        };
    }
    return { zustand: 'erledigt', hinweis: true, kurz: `Pflicht ungeklärt: ${liste}` };
};

/** Station 3. „offen" ist fachlich erlaubt; erledigt mit Vermerk, sobald am
 *  gespeicherten Satz „Rest bleibt offen" fuer genau diesen Merkmalsstand
 *  vermerkt ist (`restOffenVermerkt`) oder der Katalog schon steht — dann ist
 *  der Nutzer ohnehin weitergegangen. */
const merkmale = ({ phase, fragen }: Lage): Teilstand => {
    if (!phase) return { zustand: 'offen', kurz: '' };
    const n = offeneMerkmale(phase.merkmale).length;
    if (!n) return { zustand: 'erledigt', kurz: '' };
    if (restOffenVermerkt(phase.merkmale) || fragen.length > 0) {
        return { zustand: 'erledigt', hinweis: true, kurz: `${zahlwort(n, 'Merkmal', 'Merkmale')} offen — Fragen bleiben drin` };
    }
    return { zustand: 'offen', kurz: `${zahlwort(n, 'Merkmal', 'Merkmale')} offen` };
};

const katalog = ({ phase, fragen }: Lage): Teilstand => {
    if (!fragen.length) return { zustand: 'offen', kurz: 'Noch kein Fragenkatalog' };
    const m = (phase && phase.merkmale) || {};
    const nach = phase ? neueFragenNachziehen(fragen, m, undefined, phase.lph ?? null).length : 0;
    const weg = phase ? nichtMehrZutreffend(fragen, m).length : 0;
    let kurz = zahlwort(fragen.length, 'Frage', 'Fragen');
    if (nach > 0) kurz += ` · ${nach} nachziehbar`;
    if (weg > 0) kurz += ` · ${weg} gegenstandslos möglich`;
    return { zustand: 'erledigt', hinweis: nach > 0 || weg > 0, kurz };
};

const versenden = ({ fragen }: Lage): Teilstand => {
    if (!fragen.length) return { zustand: 'gesperrt', kurz: '', sperrgrund: SPERRGRUND_KATALOG };
    const nichtGestellt = fragen.filter(f => f.status === 'offen');
    if (!nichtGestellt.length) return { zustand: 'erledigt', kurz: 'alle Fragen gestellt' };
    const a = nichtGestellt.filter(f => f.kritikalitaet === 'A').length;
    return {
        zustand: 'offen',
        kurz: `${zahlwort(nichtGestellt.length, 'Frage', 'Fragen')} noch nicht gestellt${a ? ` (davon ${a} haftungsrelevant)` : ''}`,
    };
};

/**
 * Station 6. Abgelaufene Fristen zaehlt `faelligeStatuswechsel` — genau die
 * Zeilen, die „Abgelaufene Fristen feststellen" umstellen wuerde. Die
 * Feststellung trifft der Nutzer, nie die Automatik (Entscheid E9 der
 * Ampel). Waehrend nur Fristen laufen, `wartet` die Station; das blockiert
 * nach Entscheid E-L4 nicht, Unterlagen und Bedenken sind „waehrenddessen"
 * dran (`naechsteStation` ueberspringt `wartet`).
 */
const antworten = ({ fragen, heute }: Lage): Teilstand => {
    if (!fragen.length) return { zustand: 'gesperrt', kurz: '', sperrgrund: SPERRGRUND_KATALOG };
    const faellig = new Set(faelligeStatuswechsel(fragen, heute).map(w => w.id));
    // Teilweise beantwortet und ueber der Frist: der Sammelknopf stellt sie
    // nicht um (nur `gestellt`), aber sie warten auch nicht mehr — die Antwort
    // will bewertet werden (beantwortet oder nachfragen). Deshalb `offen`.
    const teilUeber = new Set(fragen.filter(f => f.status === 'teilbeantwortet' && istUeberfaellig(f, heute)).map(f => f.id));
    const wartend = fragen.filter(f => (f.status === 'gestellt' || f.status === 'teilbeantwortet') && !teilUeber.has(f.id));
    const teilText = teilUeber.size > 0 ? `${zahlwort(teilUeber.size, 'Teilantwort', 'Teilantworten')} über der Frist — prüfen` : '';
    if (faellig.size > 0) {
        const uebrig = wartend.filter(f => !faellig.has(f.id)).length;
        return {
            zustand: 'handlungsbedarf',
            kurz: `${zahlwort(faellig.size, 'Frist', 'Fristen')} abgelaufen — feststellen${teilText ? ` · ${teilText}` : ''}${uebrig > 0 ? ` · ${zahlwort(uebrig, 'wartet', 'warten')}` : ''}`,
        };
    }
    if (teilUeber.size > 0) {
        return {
            zustand: 'offen',
            kurz: `${teilText}${wartend.length > 0 ? ` · ${zahlwort(wartend.length, 'wartet', 'warten')}` : ''}`,
        };
    }
    if (wartend.length > 0) {
        const naechste = fruehesterTag(wartend.map(f => f.frist_am).filter(t => String(t || '').slice(0, 10) >= heute));
        return {
            zustand: 'wartet',
            naechsteFrist: naechste,
            kurz: `${zahlwort(wartend.length, 'wartet', 'warten')} auf Antwort${naechste ? `, nächste Frist ${datumDeutsch(naechste)}` : ''}`,
        };
    }
    if (fragen.every(f => f.status === 'offen')) return { zustand: 'offen', kurz: 'Noch keine Frage gestellt' };
    return { zustand: 'erledigt', kurz: `${fragen.filter(f => f.status === 'beantwortet').length} beantwortet` };
};

/** Station 7 — dieselbe Zaehlung wie die Ampel: erforderlich (Merkmal
 *  ausdruecklich ja oder im SiGe-Plan genannt) und noch offen oder
 *  angefordert. */
const unterlagen = ({ e, phase, heute }: Lage): Teilstand => {
    if (!phase) return { zustand: 'gesperrt', kurz: '', sperrgrund: SPERRGRUND_GRUNDLAGEN };
    const zeilen = baueUnterlagenZeilen({
        merkmale: phase.merkmale || {},
        eintraege: e.unterlagen ?? phase.unterlagen ?? [],
        sigeSchritte: e.sigeSchritte || [],
    });
    const fehlend = zeilen.filter(z => z.erforderlich && istUnerledigt(z.status));
    if (!fehlend.length) return { zustand: 'erledigt', kurz: 'alle erforderlichen liegen vor' };
    const ueber = fehlend.filter(z => unterlagenFristUeberfaellig(z.eintrag, heute)).length;
    if (ueber === 0 && fehlend.every(z => z.status === 'angefordert')) {
        const frist = fruehesterTag(fehlend.map(z => (z.eintrag ? z.eintrag.frist_am : null)));
        return {
            zustand: 'wartet',
            naechsteFrist: frist,
            kurz: `${fehlend.length} angefordert${frist ? `, Frist ${datumDeutsch(frist)}` : ''}`,
        };
    }
    return {
        zustand: 'offen',
        kurz: `${zahlwort(fehlend.length, 'erforderliche fehlt', 'erforderliche fehlen')}${ueber > 0 ? ` (${ueber} überfällig)` : ''}`,
    };
};

/**
 * Die A-Fragen, die eine Bedenkenanzeige verlangen: unerledigt, von keiner
 * VERSENDETEN Bedenken-/Gefahrenanzeige gedeckt und ueber der Frist oder als
 * „nicht beantwortet" festgestellt — dieselbe Zaehlung wie `ueberfaelligA` in
 * `planungAmpel.ts`. Station 8 bietet sie als Vorbelegung des Entwurfs an.
 */
export const ungedeckteAFragen = (
    fragen: readonly Planungsfrage[],
    hinweise: readonly Planungshinweis[],
    heuteIso: string,
): Planungsfrage[] => {
    const heute = String(heuteIso || '').slice(0, 10);
    const blockiert = new Set(pruefeFreigabe([...fragen], [...hinweise]).blockierendeCodes);
    return fragen.filter(f => unerledigt(f) && f.kritikalitaet === 'A' && blockiert.has(f.code)
        && (istUeberfaellig(f, heute) || f.status === 'nicht_beantwortet'));
};

/**
 * Station 8. `handlungsbedarf` zaehlt GENAU wie `ueberfaelligA` in
 * `planungAmpel.ts`: A-Frage, unerledigt, von keiner VERSENDETEN
 * Bedenkenanzeige gedeckt (`blockierendeCodes`) und entweder ueber der Frist
 * oder als „nicht beantwortet" festgestellt. Ein Entwurf deckt nicht.
 */
const bedenken = ({ fragen, hinweise, heute }: Lage): Teilstand => {
    if (!fragen.length) return { zustand: 'gesperrt', kurz: '', sperrgrund: SPERRGRUND_KATALOG };
    const freigabe = pruefeFreigabe(fragen, hinweise);
    const blockiert = new Set(freigabe.blockierendeCodes);
    const harte = ungedeckteAFragen(fragen, hinweise, heute).length;
    if (harte > 0) {
        return {
            zustand: 'handlungsbedarf',
            kurz: `${zahlwort(harte, 'haftungsrelevante Frage', 'haftungsrelevante Fragen')} ohne Deckung — Bedenkenanzeige`,
        };
    }
    // Nur Bedenken- und Gefahrenanzeigen: nur sie koennen decken
    // (`pruefeFreigabe`). Ein liegengebliebener Beratungshinweis haelt die
    // Station nicht auf.
    const entwuerfe = hinweise.filter(h => !h.versendet_am && (h.art === 'bedenken' || h.art === 'gefahr')).length;
    if (entwuerfe > 0) {
        return { zustand: 'offen', kurz: `${zahlwort(entwuerfe, 'Entwurf', 'Entwürfe')} nicht versendet` };
    }
    if (freigabe.freigabeMoeglich) {
        const versendet = hinweise.filter(h => !!h.versendet_am).length;
        return versendet > 0
            ? { zustand: 'erledigt', kurz: `${versendet} versendet` }
            : { zustand: 'nicht_erforderlich', kurz: 'keine Bedenkenanzeige nötig' };
    }
    return {
        zustand: 'nicht_erforderlich',
        kurz: `wird nötig, falls ${zahlwort(blockiert.size, 'haftungsrelevante Frage unbeantwortet bleibt', 'haftungsrelevante Fragen unbeantwortet bleiben')}`,
    };
};

/** Station 9. Offene Fassungsfragen sind nur ein Vermerk — sie halten den
 *  Abschluss nicht auf (die Rueckfrage nach 6.1 ist kein Riegel). Solange
 *  noch Antworten ausstehen, draengt der Leitfaden nicht zum Abschluss: die
 *  Station `wartet`, auch wenn das Gate offen ist — abschliessen kann man
 *  trotzdem, die offenen B/C-Fragen gehen dann in die Restrisikoliste. */
const abschluss = ({ e, phase, fragen, hinweise }: Lage): Teilstand => {
    let teil: Teilstand;
    if (phase && phase.status === 'abgeschlossen') {
        const am = datumDeutsch(phase.abgeschlossen_am);
        teil = { zustand: 'erledigt', kurz: am ? `abgeschlossen am ${am}` : 'abgeschlossen' };
    } else if (!fragen.length) {
        return { zustand: 'gesperrt', kurz: '', sperrgrund: SPERRGRUND_KATALOG };
    } else {
        const freigabe = pruefeFreigabe(fragen, hinweise);
        const offen = new Set(freigabe.blockierendeCodes).size;
        const ausstehend = fragen.filter(f => f.status === 'gestellt' || f.status === 'teilbeantwortet').length;
        teil = !freigabe.freigabeMoeglich
            ? { zustand: 'wartet', naechsteFrist: null, kurz: zahlwort(offen, 'haftungsrelevante Frage offen', 'haftungsrelevante Fragen offen') }
            : ausstehend > 0
                ? { zustand: 'wartet', naechsteFrist: null, kurz: `Abschluss möglich, ${zahlwort(ausstehend, 'Antwort steht', 'Antworten stehen')} noch aus` }
                : { zustand: 'offen', kurz: 'Abschluss möglich' };
    }
    const fassungen = e.offeneFassungen || 0;
    if (fassungen > 0) {
        teil.hinweis = true;
        teil.kurz += ` · ${zahlwort(fassungen, 'Fassungsfrage offen', 'Fassungsfragen offen')}`;
    }
    return teil;
};

const REGELN: Record<StationSchluessel, (l: Lage) => Teilstand> = {
    beschreiben, umfang, merkmale, katalog, versenden, antworten, unterlagen, bedenken, abschluss,
};

// ═══════════════════════════════════════════════════════════════════════════
// STAND, NAECHSTE STATION, WEITER-TEXT
// ═══════════════════════════════════════════════════════════════════════════

/** Aus dem Teilstand den vollen Stand: `sperrgrund` nur bei `gesperrt`,
 *  `naechsteFrist` nur bei `wartet`, sonst `null`. */
const voll = (s: StationSchluessel, t: Teilstand): StationStand => ({
    schluessel: s,
    nummer: STATION_REIHENFOLGE.indexOf(s) + 1,
    abschnitt: STATION_ABSCHNITT[s],
    zustand: t.zustand,
    kurz: t.kurz,
    hinweis: t.hinweis === true,
    sperrgrund: t.zustand === 'gesperrt' ? (t.sperrgrund ?? null) : null,
    naechsteFrist: t.zustand === 'wartet' ? (t.naechsteFrist ?? null) : null,
});

/**
 * Die Station, an der als naechstes etwas zu tun ist: erste mit
 * `handlungsbedarf` (dieselben harten Gruende wie die rote Ampel), sonst die
 * erste `aktuell`/`offen`, sonst die erste, die `wartet`; ist nichts mehr zu
 * tun, der Abschluss. `wartet` wird bewusst uebersprungen (Entscheid E-L4):
 * waehrend Antworten ausstehen, sind Unterlagen und Bedenken dran.
 */
export const naechsteStation = (staende: readonly StationStand[]): StationSchluessel => {
    const erste = (passt: (z: StationZustand) => boolean): StationSchluessel | undefined => {
        const s = staende.find(x => passt(x.zustand));
        return s ? s.schluessel : undefined;
    };
    return erste(z => z === 'handlungsbedarf')
        ?? erste(z => z === 'aktuell' || z === 'offen')
        ?? erste(z => z === 'wartet')
        ?? 'abschluss';
};

/**
 * Stand aller neun Stationen in Ablaufreihenfolge.
 *
 * Abgeschlossene Phase (Entscheid E-L11): alle Stationen `erledigt`, ohne
 * Vermerk, Sperrgrund und Frist — der Leitfaden zeigt dann nur noch, was war;
 * `kurz` bleibt als Rueckblick stehen. Sonst wird genau die Station
 * `aktuell`, die `naechsteStation` waehlt, und nur, wenn sie `offen` waere —
 * eine Station mit `handlungsbedarf` oder `wartet` behaelt ihren Zustand.
 */
export const leitfadenStand = (e: LeitfadenEingang): StationStand[] => {
    const lage: Lage = {
        e,
        heute: String(e.heuteIso || '').slice(0, 10),
        phase: e.phase,
        fragen: e.fragen || [],
        hinweise: e.hinweise || [],
    };
    const staende = STATION_REIHENFOLGE.map(s => voll(s, REGELN[s](lage)));

    if (lage.phase && lage.phase.status === 'abgeschlossen') {
        return staende.map((s): StationStand => ({ ...s, zustand: 'erledigt', hinweis: false, sperrgrund: null, naechsteFrist: null }));
    }

    const naechste = naechsteStation(staende);
    return staende.map((s): StationStand => (s.schluessel === naechste && s.zustand === 'offen' ? { ...s, zustand: 'aktuell' } : s));
};

/** Zustaende, in denen eine gemerkte Station beim Wiedereinstieg noch etwas
 *  bietet. Erledigte, gesperrte und nicht noetige fallen auf die berechnete
 *  Station zurueck. */
const MERKBAR: ReadonlyArray<StationZustand> = ['aktuell', 'offen', 'wartet', 'handlungsbedarf'];

/** Station beim Oeffnen: die zuletzt besuchte, solange dort noch etwas offen
 *  ist, sonst `naechsteStation` (Plan 2.2). */
export const startStation = (
    gemerkt: StationSchluessel | null | undefined,
    staende: readonly StationStand[],
): StationSchluessel => {
    if (istStationSchluessel(gemerkt)) {
        const s = staende.find(x => x.schluessel === gemerkt);
        if (s && MERKBAR.includes(s.zustand)) return gemerkt;
    }
    return naechsteStation(staende);
};

/**
 * Eine Zeile fuer Hub-Kachel und Aufgabenliste: „Weiter: Antworten
 * einarbeiten · 2 Fristen abgelaufen — feststellen" bzw. „Wartet: …", wenn
 * nur noch Fristen laufen. Leer, wenn nichts mehr zu tun ist.
 */
export const weiterText = (staende: readonly StationStand[]): string => {
    if (staende.every(s => s.zustand === 'erledigt')) return '';
    const s = naechsteStation(staende);
    const stand = staende.find(x => x.schluessel === s);
    // Rueckfall `abschluss` ohne offene Station: nichts, wozu man weiter koennte.
    if (!stand || stand.zustand === 'erledigt' || stand.zustand === 'nicht_erforderlich') return '';
    const kopf = `${stand.zustand === 'wartet' ? 'Wartet' : 'Weiter'}: ${STATION_TEXTE[s].kurzTitel}`;
    return stand.kurz ? `${kopf} · ${stand.kurz}` : kopf;
};
